import { ReactNode } from 'react';
import { previewEmail } from '../../../../utils/email/previewEmail';
import { formatDate } from '../../../../utils/dateTime';

/**
 * Shared column factory used by both EmailsStatsModal (Opens drill-down
 * from the email tile) and EmailEngagementStatsModal (Opens / Clicks
 * widget drill-down). The shape is identical — name link, subject
 * preview button, sent-at timestamp — so we factor it into one place.
 */
export function buildEmailColumns(opts: {
  timezone?: string;
  onOpenContact: (contactId: string) => void;
}): Array<{
  key: string;
  header: string;
  width?: string;
  cell: (item: Record<string, unknown>) => ReactNode;
}> {
  const { timezone, onOpenContact } = opts;
  return [
    {
      key: 'contactName',
      header: 'Name',
      width: '200px',
      cell: (item) => {
        const name = item.contactName as string | undefined;
        const contactId = item.contactId as string | undefined;
        const display = name && name !== 'unknown' ? name : '—';
        if (!contactId) {
          return <span className="font-medium text-muted-foreground">{display}</span>;
        }
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenContact(contactId);
            }}
            className="text-primaryfortext font-medium underline text-left"
          >
            {display}
          </button>
        );
      },
    },
    {
      key: 'subject',
      header: 'Subject',
      width: '250px',
      cell: (item) => {
        const html = item.bodyHtml as string | null;
        const subject = item.subject as string | undefined;
        const label = subject || '—';
        if (html) {
          return (
            <button
              onClick={() =>
                previewEmail({
                  bodyHtml: html,
                  subject,
                  contactName: item.contactName as string | undefined,
                  contactEmail: item.contactEmail as string | undefined,
                  toAddress: (item.contactEmail || item.toAddress) as string | undefined,
                  sentAt: item.sentAt as string | undefined,
                  templateName: item.templateName as string | undefined,
                  direction: 'outbound',
                })
              }
              className="truncate block max-w-[240px] text-primaryfortext underline decoration-dotted underline-offset-2 text-left hover:text-primary/80"
            >
              {label}
            </button>
          );
        }
        return <span className="truncate block max-w-[240px]">{label}</span>;
      },
    },
    {
      key: 'sentAt',
      header: 'Date/Time',
      width: '220px',
      cell: (item) => {
        const ts = item.sentAt as string | undefined;
        return ts ? (
          <span className="whitespace-nowrap text-foreground">{formatDate(ts, timezone)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
  ];
}
